import type { MeetingListParams } from '../api'

/**
 * `id` is what the dropdown keys selection on; `value`/`leaveReason` are what
 * gets sent to the API. The four failure sub-reasons all carry
 * `value: 'failed'` — they are `status=failed` narrowed by `leaveReason`, not
 * distinct `MeetingStatus` values (see meetingStatusMap.ts) — so `id` has to
 * be unique on its own for the four to render as separate, independently
 * selectable rows.
 *
 * Kept in its own module (not MeetingFilters.tsx) because it has no
 * component to export alongside it — a .tsx file mixing a component export
 * with plain data/function exports breaks Fast Refresh (react-refresh/only-
 * export-components), which fails CI's `eslint --max-warnings 0`.
 */
export const STATUSES: { id: string; value: string; leaveReason?: string; key: string }[] = [
  { id: 'all', value: 'all', key: 'meetings.status.all' },
  { id: 'ready', value: 'ready', key: 'meetings.status.ready' },
  { id: 'processing', value: 'processing', key: 'meetings.status.processing' },
  { id: 'recording', value: 'recording', key: 'meetings.status.recording' },
  { id: 'scheduled', value: 'scheduled', key: 'meetings.status.scheduled' },
  { id: 'failed', value: 'failed', key: 'meetings.status.failed' },
  { id: 'failed-denied', value: 'failed', leaveReason: 'denied', key: 'meetings.status.denied' },
  {
    id: 'failed-not_admitted',
    value: 'failed',
    leaveReason: 'not_admitted',
    key: 'meetings.status.notAdmitted',
  },
  {
    id: 'failed-join_failed',
    value: 'failed',
    leaveReason: 'join_failed',
    key: 'meetings.status.joinFailed',
  },
  { id: 'skipped', value: 'skipped', key: 'meetings.status.skipped' },
  { id: 'no_audio', value: 'no_audio', key: 'meetings.status.noAudio' },
]

export type StatusOption = (typeof STATUSES)[number]

/** 'all' is the URL/param sentinel for "no leave-reason filter" — the same
 * convention every other filter here uses instead of undefined. Exported
 * (with the two helpers below) so selecting an option is pinned by a test
 * with no rendering involved, the same reasoning as
 * `resolveMeetingStatusBadge` in meetingStatusMap.ts. */
export function currentLeaveReasonOf(params: Pick<MeetingListParams, 'leaveReason'>) {
  return params.leaveReason && params.leaveReason !== 'all' ? params.leaveReason : undefined
}

/** Whether a Status dropdown row is the one the current params select. */
export function isStatusOptionSelected(
  option: StatusOption,
  params: Pick<MeetingListParams, 'status' | 'leaveReason'>,
): boolean {
  return (params.status ?? 'all') === option.value && currentLeaveReasonOf(params) === option.leaveReason
}

/** Picking a Status option, including one of the four new failure reasons,
 * always resets to page 1 — the same rule every other filter here follows. */
export function statusOptionPatch(option: StatusOption): Partial<MeetingListParams> {
  return { status: option.value, leaveReason: option.leaveReason ?? 'all', page: 1 }
}
